<!-- Выбирает транспорт с поиском, preselected option и фильтром автопарка. -->
<template>
  <BaseSelect
    ref="selectRef"
    v-bind="$attrs"
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    :editable="editable"
    :edit-title="editTitle"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @edit-option="openEditModal"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />

  <AddVehicleModal
    v-if="isEditModalVisible"
    :vehicle-id="editingVehicleId"
    @vehicle-updated="handleVehicleUpdated"
    @close="closeEditModal"
  />
</template>

<script>
import { mapState } from "pinia";

import { useVehicleStore } from "@/stores/vehicleStore";
import AddVehicleModal from "@/components/Vehicle/AddVehicleModal.vue";

export default {
  name: "VehicleSelect",

  inheritAttrs: false,

  components: {
    AddVehicleModal,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите транспорт",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Добавить новый транспорт",
    },
    noResultsText: {
      type: String,
      default: "Транспорт не найден",
    },
    excludeIds: {
      type: Array,
      default: () => [],
    },
    fleetParkId: {
      type: [String, Number],
      default: null,
    },
    vehicleType: {
      type: String,
      default: "",
    },
    edoVehicleRole: {
      type: String,
      default: "",
    },
    editable: {
      type: Boolean,
      default: true,
    },
    showFleetPark: {
      type: Boolean,
      default: false,
    },
    // Прокидывается в BaseSelect для кнопки редактирования выбранного транспорта
    editTitle: {
      type: String,
      default: "Редактировать транспорт",
    },
  },

  emits: [
    "update:modelValue",
    "change",
    "vehicle-change",
    "button-click",
    "edit-option",
    "vehicle-updated",
  ],

  data() {
    return {
      preselectedOption: null,
      isSearchActive: false,
      isEditModalVisible: false,
      editingVehicleId: null,
    };
  },

  computed: {
    ...mapState(useVehicleStore, [
      "vehicles",
      "pagination",
      "dataIsLoading",
    ]),

    formattedOptions() {
      const options = this.vehicles
        // Фильтруем
        .filter((vehicle) => {
          if (this.modelValue && vehicle.id === this.modelValue)
            return true;
          return !this.excludeIds.includes(vehicle.id);
        })
        .map((vehicle) => ({
          value: vehicle.id,
          label: this.vehicleOptionLabel(vehicle),
          fleet_park_id: vehicle.fleet_park_id || null,
          fleet_park_name: vehicle.fleet_park?.name || null,
        }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (
        !this.isSearchActive &&
        String(this.preselectedOption?.value) !== String(newVal)
      ) {
        await this.fetchPreselectedOption();
      }
    },
    async fleetParkId() {
      const store = useVehicleStore();
      await store.fetchVehicles(this.vehicleFilters());
    },
  },

  methods: {
    vehicleOptionLabel(vehicle) {
      if (!this.showFleetPark) return vehicle.selector_name;

      const fleetParkLabel = vehicle.fleet_park?.name || "Без автопарка";
      return `${vehicle.selector_name} · ${fleetParkLabel}`;
    },

    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
      this.$emit("vehicle-change", option || null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = useVehicleStore();
      await store.fetchVehicles({
        ...this.vehicleFilters(),
        search: searchData.query,
      });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = useVehicleStore();
      await store.fetchVehicles(this.vehicleFilters());
    },

    async handleScrollEnd() {
      const store = useVehicleStore();
      if (
        store.pagination.page < store.pagination.pages &&
        !store.dataIsLoading
      ) {
        await store.loadMoreVehicles();
      }
    },

    openEditModal(vehicleId) {
      if (!vehicleId) return;

      this.editingVehicleId = vehicleId;
      this.isEditModalVisible = true;
      this.$emit("edit-option", vehicleId);
    },

    closeEditModal() {
      this.isEditModalVisible = false;
      this.editingVehicleId = null;
    },

    handleVehicleUpdated(vehicle) {
      const store = useVehicleStore();
      store.replaceVehicle(vehicle);
      this.applyPreselectedVehicle(vehicle);

      this.$nextTick(() => {
        this.$refs.selectRef?.reselectSelectedOption();
      });
      this.$emit("vehicle-updated", vehicle);
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useVehicleStore();
      const exists = store.vehicles.find(
        (v) => String(v.id) === String(this.modelValue)
      );
      if (exists) {
        this.applyPreselectedVehicle(exists);
        return;
      }

      const vehicle = await store.fetchVehicleSelectItem(this.modelValue);
      this.applyPreselectedVehicle(vehicle);
    },

    applyPreselectedVehicle(vehicle) {
      const option = {
        value: vehicle.id,
        label: this.vehicleOptionLabel(vehicle),
        fleet_park_id: vehicle.fleet_park_id || null,
        fleet_park_name: vehicle.fleet_park?.name || null,
      };

      this.preselectedOption = option;
      this.$emit("vehicle-change", option);
    },

    vehicleFilters() {
      return {
        vehicleType: this.vehicleType,
        edoVehicleRole: this.edoVehicleRole,
        fleetParkId: this.fleetParkId,
      };
    },
  },

  async mounted() {
    const store = useVehicleStore();
    await store.fetchVehicles(this.vehicleFilters());
    await this.fetchPreselectedOption();
  },
};
</script>
